import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus } from 'lucide-react';
import {
  PageBody,
  ViewTab,
  ViewTabList,
  buttonVariants,
  iconButtonVariants,
} from '@wundo/ui';
import type { RoleplayCard, RoleplayListResponse } from '@wundo/shared/roleplay.ts';
import { PageTitle } from '@/components/PageTitle.tsx';
import type { PracticeCardProps } from '@/components/practice/PracticeCard.tsx';
import {
  PracticeCardList,
  type PracticeTab,
} from '@/components/practice/PracticeCardList.tsx';
import { PracticeFeedbackDialog } from '@/components/practice/PracticeFeedbackDialog.tsx';
import { PracticeSessionModal } from '@/components/practice/PracticeSessionModal.tsx';
import { thisWeekFirst } from '@/components/practice/thisWeekFirst.ts';
import { reorderRoleplays, startRoleplaySession } from '@/lib/roleplays.ts';
import { PILOT_CONTEXT_KEY, fetchPilotContext } from '@wundo/client/pilot.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { usePublishChatAnchor } from '@/lib/chatAnchor.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { roleplaysQuery } from '@/lib/roleplaysQuery.ts';
import { useDemoHoldBack, type HeldBackProps } from '@/lib/useDemoHoldBack.ts';
import { m } from '@/paraglide/messages.js';

/** What the page can be asked to open with. */
interface PracticeSearch {
  /** Show this roleplay's feedback straight away. */
  feedback?: string;
  /** Hold this practice conversation open over the page. */
  session?: string;
}

export const Route = createFileRoute('/_app/practice/')({
  /*
   * A practice conversation ends by coming back here with the feedback named,
   * so the debrief is read where the earlier attempts are. A running one is
   * named too, so a reload mid-conversation opens the page with the
   * conversation back over it rather than dropping the call.
   */
  validateSearch: (search: Record<string, unknown>): PracticeSearch => ({
    feedback: typeof search.feedback === 'string' ? search.feedback : undefined,
    session: typeof search.session === 'string' ? search.session : undefined,
  }),
  component: PracticeRoute,
});

function PracticeRoute() {
  const { feedback, session } = Route.useSearch();
  return <PracticePage feedbackRoleplayId={feedback} sessionId={session} />;
}

export interface PracticePageProps {
  /** Opens this roleplay's feedback on arrival. */
  feedbackRoleplayId?: string;
  /** Arrives with this roleplay's card unfolded. */
  openRoleplayId?: string;
  /** Arrives with this practice conversation already open over the page. */
  sessionId?: string;
  /** Starts a practice of this roleplay on arrival, as its start button would. */
  startRoleplayId?: string;
}

/**
 * Oefenen: the conversations you can practise before you have them. The
 * library is what the organisation put there for everybody; "Voor mijn
 * gesprekken" was made for you alone, out of your own chat. An admin sees the
 * same page, with the drafts marked and the way to add one.
 */
export function PracticePage({
  feedbackRoleplayId,
  openRoleplayId,
  sessionId,
  startRoleplayId,
}: PracticePageProps = {}) {
  /* The page is the whole anchor: everything on it is the reader's own. */
  usePublishChatAnchor({ type: 'practice' });

  const navigate = useNavigate();
  /* Null until a tab is clicked: until then the address decides which one. */
  const [pickedTab, setPickedTab] = useState<PracticeTab | null>(null);
  /* One card at a time; the accordion hands back the open one as a list. */
  const [openIds, setOpenIds] = useState<string[]>(
    openRoleplayId ? [openRoleplayId] : [],
  );
  /*
   * Whose feedback is showing, and the practice conversation held over the
   * page; null when there is none. Read out of the address on arrival and kept
   * here afterwards, so a stray Back cannot take away a live microphone.
   */
  const [feedbackId, setFeedbackId] = useState<string | null>(feedbackRoleplayId ?? null);
  const [liveSessionId, setLiveSessionId] = useState<string | null>(sessionId ?? null);

  const lists = usePracticeLists();
  /* A demo practises the library as it is: writing or arranging conversations is held back. */
  const holdBack = useDemoHoldBack();
  usePracticeQuickActions(lists.all);

  /*
   * Starting hands back a session and the conversation opens over the page, so
   * the list keeps its tab, its scroll and whichever card was open.
   */
  const start = useMutation({
    mutationFn: (roleplayId: string) => startRoleplaySession(roleplayId),
    onSuccess: (session) => {
      setLiveSessionId(session.id);
      void navigate({ to: '/practice', search: { session: session.id }, replace: true });
    },
  });
  useStartOnArrival(startRoleplayId, lists.data, start.mutate);
  const reorder = useLibraryReorder();

  const feedbackFor = lists.all.find((candidate) => candidate.id === feedbackId) ?? null;

  /* Arriving on your own practice conversation opens the tab it is on. */
  const arrived = lists.all.find(
    (candidate) => candidate.id === feedbackRoleplayId || candidate.id === openRoleplayId,
  );
  const tab: PracticeTab = lists.demo
    ? 'library'
    : (pickedTab ?? (arrived?.kind === 'personal' ? 'personal' : 'library'));

  function cardProps(card: RoleplayCard): Omit<PracticeCardProps, 'handle'> {
    return {
      card,
      starting: start.isPending,
      expanded: openIds.includes(card.id),
      canManage: lists.canManage,
      /*
       * Only the library is authored. A personal roleplay came out of
       * somebody's own chat, and nobody edits it — not even an admin, which
       * the API enforces too.
       */
      edit:
        lists.canManage && card.kind === 'library' ? (
          <PracticeEditLink card={card} heldBack={holdBack(true)} />
        ) : undefined,
      onStart: () => startPractice(card.id),
      onOpenFeedback: () => setFeedbackId(card.id),
    };
  }

  function startPractice(roleplayId: string) {
    setFeedbackId(null);
    start.mutate(roleplayId);
  }

  function pickTab(next: PracticeTab) {
    setPickedTab(next);
    setOpenIds([]);
  }

  /*
   * The conversation is done with: it closes over the page where it was left.
   * Stable, because a session that was already finished when it opened hands
   * over its feedback the moment it is mounted.
   */
  const closeSession = useCallback(() => {
    setLiveSessionId(null);
    void navigate({ to: '/practice', search: {}, replace: true });
  }, [navigate]);

  /* The link is a way back to this page, not to this dialog. */
  function closeFeedback() {
    setFeedbackId(null);
    if (feedbackRoleplayId !== undefined) {
      void navigate({ to: '/practice', search: {}, replace: true });
    }
  }

  return (
    <>
      <PageTitle>{m.nav_practice()}</PageTitle>
      <PageBody>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <ViewTabList aria-label={m.practice_tabs()}>
            <ViewTab active={tab === 'library'} onClick={() => pickTab('library')}>
              {m.practice_tab_library()}
            </ViewTab>
            {!lists.demo && (
              <ViewTab active={tab === 'personal'} onClick={() => pickTab('personal')}>
                {m.practice_tab_personal()}
              </ViewTab>
            )}
          </ViewTabList>

          {lists.canManage && (
            <Link to='/practice/new' className={buttonVariants()} {...holdBack(true)}>
              <Plus className='size-3.5' aria-hidden='true' />
              {m.practice_new()}
            </Link>
          )}
        </div>

        <PracticeCardList
          tab={tab}
          cards={tab === 'library' ? lists.library : lists.personal}
          loading={lists.isPending}
          failed={lists.isError}
          startFailed={start.isError}
          moveFailed={reorder.isError}
          sortable={lists.canManage && tab === 'library' && !lists.demo}
          openIds={openIds}
          onOpenChange={setOpenIds}
          cardProps={cardProps}
          onReorder={(ids) => reorder.mutate(ids)}
        />

        {/*
          The one thing people ask before they say anything out loud here.
          Under the list rather than over it: it is a reassurance, not an
          instruction, and nobody should have to read past it to reach the page.
        */}
        <p className='mt-6.5 flex max-w-185 items-start gap-2.5 border-t border-line pt-4 text-base leading-relaxed text-ink-muted'>
          <Lock className='mt-0.75 size-3.75 shrink-0' aria-hidden='true' />
          <span>{m.practice_privacy()}</span>
        </p>
      </PageBody>

      <PracticeFeedbackDialog
        card={feedbackFor}
        starting={start.isPending}
        onClose={closeFeedback}
        onPractiseAgain={() => feedbackFor && startPractice(feedbackFor.id)}
      />

      {liveSessionId !== null && (
        <PracticeSessionModal
          key={liveSessionId}
          sessionId={liveSessionId}
          onLeave={closeSession}
          starting={start.isPending}
          startFailed={start.isError}
          onPractiseAgain={startPractice}
        />
      )}
    </>
  );
}

function PracticeEditLink({
  card,
  heldBack,
}: {
  card: RoleplayCard;
  heldBack: HeldBackProps;
}) {
  return (
    <Link
      to='/practice/$roleplayId/edit'
      params={{ roleplayId: card.id }}
      aria-label={m.practice_edit({ title: card.title })}
      className={iconButtonVariants({
        shape: 'square',
        className: 'bg-fill text-ink-muted hover:bg-line hover:text-ink',
      })}
      {...heldBack}
    >
      <Pencil className='size-3.75' aria-hidden='true' />
    </Link>
  );
}

/**
 * The two lists on the page. A demo organisation has no chat to make a
 * personal one from, so the library is all it gets. A pilot member finds this
 * week's card on top — never an admin, whose next move would save the pin.
 */
function usePracticeLists() {
  const { data, isPending, isError } = useQuery(roleplaysQuery());
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  const canManage = data?.can_manage === true;
  const thisWeekId = pilot?.practice?.id ?? null;
  const library = thisWeekFirst(data?.library ?? [], canManage ? null : thisWeekId);
  const personal = data?.personal ?? [];

  return {
    data,
    isPending,
    isError,
    demo: user?.demo === true,
    canManage,
    library,
    personal,
    all: [...library, ...personal],
  };
}

/*
 * This page's conversation starters (DEV-16, DEV-97). Asking what you learned
 * needs a practice conversation you actually finished — counted per user and
 * only over finished sessions — so a list still loading is not offered it.
 */
function usePracticeQuickActions(cards: RoleplayCard[]) {
  const practised = cards.some((card) => card.attempt_count > 0);

  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_practice_which_first(), icon: 'roleplay' },
      { text: m.chat_quick_practice_which_skill(), icon: 'tips' },
      { text: m.chat_quick_practice_own(), icon: 'prepare' },
      ...(practised
        ? [{ text: m.chat_quick_practice_last_learned(), icon: 'recap' as const }]
        : []),
    ],
  });
}

/*
 * Started on arrival, never when the link was sent: a card sent days ahead
 * would find its session gone. The address loses its `start` first, so a
 * reload or Back cannot start a second one; a start that fails leaves the
 * card unfolded with the reason.
 */
function useStartOnArrival(
  roleplayId: string | undefined,
  data: RoleplayListResponse | undefined,
  start: (roleplayId: string) => void,
) {
  const navigate = useNavigate();
  const startedOnArrival = useRef(false);

  useEffect(() => {
    if (!roleplayId || !data || startedOnArrival.current) return;
    startedOnArrival.current = true;

    void navigate({
      to: '/practice/$roleplayId',
      params: { roleplayId },
      search: {},
      replace: true,
    });
    if ([...data.library, ...data.personal].some((card) => card.id === roleplayId)) {
      start(roleplayId);
    }
  }, [roleplayId, data, navigate, start]);
}

/*
 * Arranging the library. The list is written on the page before the request is
 * answered, and the whole order is sent every time, so what is stored is the
 * list that was on the screen. A refused save puts the old order back.
 */
function useLibraryReorder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => reorderRoleplays(ids),
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['roleplays'] });
      const previous = queryClient.getQueryData<RoleplayListResponse>(['roleplays']);

      if (previous !== undefined) {
        queryClient.setQueryData<RoleplayListResponse>(['roleplays'], {
          ...previous,
          library: ids
            .map((id) => previous.library.find((card) => card.id === id))
            .filter((card) => card !== undefined),
        });
      }

      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['roleplays'], context.previous);
      }
    },
    /* Right or wrong, the server has the last word on the order. */
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['roleplays'] }),
  });
}
